import {IDrawPredictorStore} from "apps/DRAW_PREDICTOR/modules/types/store";
import {createSelector} from "reselect";
import {chain, chunk, filter, find, findKey, get} from "lodash";
import {
	IPrediction,
	IPreDrawPick,
	IPreDrawPickQuestion,
} from "apps/DRAW_PREDICTOR/modules/reducers";
import {getQualifierSquads} from "apps/DRAW_PREDICTOR/modules/selectors/projectJsons";
import {Groups, Pots} from "apps/DRAW_PREDICTOR/modules/enums";
import {
	getActivePot,
	getAllPredictions,
	getAvailableGroupForPot1,
	getPredictionByPositionAndGroup,
	getPredictionsByGroups,
	getSelectedSquadId,
	getSquadForEditId,
} from "apps/DRAW_PREDICTOR/modules/selectors/predictions";
import {LOCKED_PREDICTION} from "apps/DRAW_PREDICTOR/modules/constants";

export const getPreDrawPicks = (state: IDrawPredictorStore) => state.preDraw.picks;

export const getPreDrawActiveIndex = (state: IDrawPredictorStore) => state.preDraw.activeIndex;

export const isAllPreDrawPicksSelected = createSelector(
	getPreDrawPicks,
	(picks: IPreDrawPick[]) => {
		return Boolean(picks.length && !filter(picks, (pick) => !pick.allPicked).length);
	}
);

export const getPickedOptions = createSelector(getPreDrawPicks, (picks) => {
	return chain(picks)
		.map((pick) => pick.questions)
		.flatten()
		.map((question: IPreDrawPickQuestion) => question.selected)
		.value();
});

export const hasMadeFirstSelection = createSelector(getPickedOptions, (options) => {
	return chain(options).compact().size().gt(0).value();
});

export const getPreDrawResults = createSelector(
	getPickedOptions,
	getQualifierSquads,
	(options, squads) => {
		return chain(squads)
			.filter((squad) => {
				return squad.is_qualified || options.includes(squad.id);
			})
			.sortBy((squad) => {
				if (squad.id === 1) {
					return squad.id;
				}
				return squad.rank ?? 0;
			})
			.chunk(8)
			.map((squadArr, index) =>
				squadArr.map((squad) => ({
					...squad,
					pot: index + 1,
				}))
			)
			.flatten()
			.value();
	}
);

export const getLiteSquadsByPots = createSelector(getPreDrawResults, (squads) => {
	const chunks = chunk(squads, 8);

	return {
		[Pots.POT1]: chunks[0],
		[Pots.POT2]: chunks[1],
		[Pots.POT3]: chunks[2],
		[Pots.POT4]: chunks[3],
	};
});

export const isPreDrawSaved = (state: IDrawPredictorStore) => state.preDraw.saved;

export const getSquadByPositionAndGroupLite = createSelector(
	getPreDrawResults,
	getPredictionByPositionAndGroup,
	(squads, getSelectedPrediction) => (position: number, group: Groups) => {
		const selectedPrediction = getSelectedPrediction(position, group);

		if (selectedPrediction) {
			return find(squads, (squad) => squad.id === selectedPrediction.squadId) || null;
		}

		return null;
	}
);

export const checkIsAllowedForEditLite = createSelector(
	getSquadForEditId,
	getPreDrawResults,
	getAllPredictions,
	(squadIdForEdit, squads, allPrediction) => (squadId: number, pot: number) => {
		// Get squad by squad id
		const selectedSquad = find(squads, (squad) => squad.id === squadIdForEdit);

		// Get prediction by squad id
		const selectedPrediction = find(
			allPrediction,
			(prediction) => prediction.squadId === squadIdForEdit
		);

		const currentPredictionData = find(
			allPrediction,
			(prediction) => prediction.squadId === squadId
		);

		if (
			squadIdForEdit === squadId ||
			!selectedSquad ||
			!selectedPrediction ||
			!currentPredictionData ||
			LOCKED_PREDICTION.squadId === squadId
		) {
			return false;
		}

		return (
			pot === selectedSquad.pot /*&&
			checkPrediction(selectedSquad.confederation, currentPredictionData.namegroup, squadId)*/
		);
	}
);

export const checkIsPotFullLite = createSelector(
	getAllPredictions,
	getLiteSquadsByPots,
	(predictions, squadsByPot) => (position: number) => {
		const squadsInCurrentPot = get(squadsByPot, position);
		return (
			predictions.filter((prediction) =>
				find(squadsInCurrentPot, (squad) => prediction.squadId === squad.id)
			).length === 8
		);
	}
);

export const getAvailableGroupForActivePotLite = createSelector(
	getActivePot,
	getPredictionsByGroups,
	getPreDrawResults,
	getSelectedSquadId,
	getLiteSquadsByPots,
	(activePot, predictionsByGroups, squads, selectedSquadId, squadsByPots) => {
		// Get squad by squad id
		const selectedSquad = find(squads, (squad) => squad.id === selectedSquadId) || null;

		const findOtherSquadFromCurrentPot = get(squadsByPots, activePot);

		return (
			findKey(
				predictionsByGroups,
				(predictions) =>
					selectedSquad &&
					!findOtherSquadFromCurrentPot.find((squad) =>
						find(
							predictions,
							(prediction: IPrediction) => squad.id === prediction.squadId
						)
					)
			) || null
		);
	}
);

export const checkIsSelectedSquadByPotLite = createSelector(
	getActivePot,
	getAvailableGroupForPot1,
	getAvailableGroupForActivePotLite,
	getSelectedSquadId,
	(activePot, availableGroupForPot1, availableGroupForActivePot, selectedSquad) =>
		(position: number, group: string) => {
			// This logic only for Pot 1.
			if (activePot === 1) {
				return position === 1 && availableGroupForPot1 === group;
			}

			// This logic for Pots 2-4.
			if (!selectedSquad || position === 1) {
				return false;
			}

			return availableGroupForActivePot === group;
		}
);

export const getPredictionAndGroupBySquadIdLite = createSelector(
	getAllPredictions,
	getQualifierSquads,
	(predictions, squads) => (squadId: number | null) => ({
		prediction: predictions.find((prediction) => prediction.squadId === squadId) || null,
		squad: squads.find((prediction) => prediction.id === squadId) || null,
	})
);
